import moment from 'moment';
import { State } from '@meraki-internal/state';
import { IPaymentTerm, RevenueCatModel } from './revenue-cat/RevenueCatModel';
import { OperatorState } from '../profile/OperatorState';
import { RelativeURL } from '../support/RelativeURL';
import { BufferedEventBus } from '../events/BufferedEventBus';
import { DevSettings } from '../support/DevSettings';
import { Clock } from '../support/Clock';

// year end 2024
const PROMOTION_START = '2024-12-18T06:00:00.000Z';
const PROMOTION_END = '2025-01-02T06:00:00.000Z';
const PROMOTION_SAVING = '40%';

export type ITriggerPaywallSource =
    'new-user' |
    'menu button' |
    'weather summary' |
    'subscription expired' |
    'nudge' |
    'windy' |
    'bulk-export' |
    'mission-export' |
    'log entry' |
    'equipment card' |
    'equipment detail' |
    'equipment default';

export interface IPaywallDialogState {
    isOpen: boolean;
    isWorking: boolean;
    paymentTerm: IPaymentTerm;
}

export class PaywallDialogViewModel extends State<IPaywallDialogState> {

    private _returnTo: string | undefined;
    private _triggerSource: ITriggerPaywallSource | undefined;

    static inject = () => [
        RevenueCatModel,
        OperatorState,
        BufferedEventBus,
        DevSettings,
        Clock
    ];
    constructor(
        private revenueCat: RevenueCatModel,
        private operatorState: OperatorState,
        private events: BufferedEventBus,
        private devSettings: DevSettings,
        private clock: Clock
    ) {
        super({ isOpen: false, isWorking: false, paymentTerm: 'annual' });

        // trigger state emit at promotion start and end so dialog re-renders
        for (const when of [PROMOTION_START, PROMOTION_END]) {
            const msUntil = moment(when).diff(moment(this.clock.now()));
            if (msUntil > 0) {
                setTimeout(() => this.setState({}), msUntil + 1);
            }
        }
    }

    isNewUser = () => {
        return (this.devSettings.forceNewUserPaywall || this.getTriggerSource() === 'new-user');
    };

    isPromotionActive = () => {
        return (this.devSettings.forcePaywallPromotion || moment(this.clock.now()).isBetween(PROMOTION_START, PROMOTION_END));
    };

    getPromotionSaving = () => PROMOTION_SAVING;

    getReturnToUrl = ({ term }: { term: IPaymentTerm }) => {
        const url = new RelativeURL(this._returnTo); // defaults to current page
        url.searchParams.set('paywallTrigger', this._triggerSource || '');
        url.searchParams.set('paywallTerm', term);
        return url.toString();
    };

    open = ({ triggerSource, returnTo }: { triggerSource: ITriggerPaywallSource, returnTo?: string  }) => {
        if (this.state.isOpen) {
            return;
        }

        this.events.emit('PaywallOpened');

        this._returnTo = returnTo;
        this._triggerSource = triggerSource;

        this.setState({ isOpen: true });
    };

    getTriggerSource = () => this._triggerSource;

    resume = async ({ paymentTerm }: { paymentTerm: IPaymentTerm }): Promise<boolean> => {
        return new Promise(async resolve => {
            try {
                this.setState({ isWorking: true, isOpen: true, paymentTerm });

                const unsubscribe = this.subscribe(() => {
                    if (!this.state.isOpen) {
                        unsubscribe();
                        resolve(this.revenueCat.hasUpgraded());
                    }
                });

                // if login was successful and we are no longer a guest,
                // auto-launch the os-level store purchase dialog
                if (!this.operatorState.isGuest() && this.revenueCat.canUpgrade()) {
                    await this.revenueCat.upgrade(paymentTerm);
                }

            } catch (e) {
                // assume it's clear to the user what went wrong
                // from the messaging in the purchase dialog
            } finally {
                this.setState({ isWorking: false });
            }
        });
    };

    close = () => {
        this._returnTo = undefined;
        this._triggerSource = undefined;
        this.setState({ isOpen: false });
    };

    requirePurchase = async ({ triggerSource, returnTo }: { triggerSource: ITriggerPaywallSource, returnTo?: string  }): Promise<boolean> => {
        if (this.revenueCat.hasUpgraded()) {
            return true;
        }

        return new Promise((resolve, reject) => {
            try {
                this.open({ triggerSource, returnTo });

                // check in case paywall dialog didn't open
                if (!this.state.isOpen) {
                    resolve(false);
                    return;
                }

                const unsubscribe = this.subscribe(() => {
                    if (!this.state.isOpen) {
                        unsubscribe();
                        resolve(this.revenueCat.hasUpgraded());
                    }
                });
            } catch (e) {
                reject(e);
            }
        });
    };
}
